import { useEffect, useRef } from 'react'

// useLockAwareFetch is a custom React hook that provides a fetch function which is aware of the device's lock state.
// It uses heuristics based on visibility changes and network errors to determine if the device is likely locked and handles fetch calls accordingly.
export default function useLockAwareFetch({
  timeOut = 10_000, // 10 seconds
  lockHeuristicWindow = 30_000, // 30 seconds
  options = {},
}) {
  // visibility tracking
  const lastHiddenAt = useRef(0)
  const lastVisibleAt = useRef(new Date().getTime())

  useEffect(() => {
    // Track the last hidden and visible times. If the time between visibility changes exceeds 30,000ms (LOCK_HEURISTIC_WINDOW), assume the screen was locked and restrict fetch calls.
    const handleVisibilityChange = () => {
      if (document.hidden) {
        lastHiddenAt.current = new Date().getTime()
      } else {
        lastVisibleAt.current = new Date().getTime()
      }
    }

    handleVisibilityChange() // Initialize the visibility state on mount

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // fetchWithTimeout attaches an AbortController to the fetch request and cancels it if the response does not arrive within the specified timeout.
  const fetchWithTimeout = async (url, opts = options, timeout = timeOut) => {
    const abortController = new AbortController()

    const timeoutId = setTimeout(() => abortController.abort(), timeout)

    return fetch(url, { ...opts, signal: abortController.signal }).finally(() =>
      clearTimeout(timeoutId)
    )
  }

  // verifyScreenLock checks if the device is likely locked based on the following conditions:
  const verifyScreenLock = withInHeuristicWindow => {
    // 1) navigator.onLine === true: device thinks it's on-line
    // 2) document.hidden === true: page is currently not visible
    // 3) lastHiddenAt was recent
    if (!navigator.onLine) return false
    if (!document.hidden) return false

    const now = new Date().getTime()

    if (withInHeuristicWindow && now - lastHiddenAt.current <= lockHeuristicWindow) {
      return true
    } else if (!withInHeuristicWindow && now - lastHiddenAt.current > lockHeuristicWindow) {
      return true
    }
    return false
  }

  // lockAwareFetch wraps fetchWithTimeout and adds logic to detect and handle the "phone locked" case. If a fetch fails due to a network error while the device is likely locked, it waits for the device to become visible again before retrying the fetch.
  const lockAwareFetch = async (url, opts = options, timeout = timeOut) => {
    try {
      const res = await fetchWithTimeout(url, opts, timeout)

      // note: fetch only rejects on network-level failures, not HTTP errors
      if (!res.ok) {
        // backend responded with error status (reached BE)
        throw { kind: 'http', status: res.status, response: res }
      }

      return await res.json() // or res.text(), etc.
    } catch (error) {
      // aborted by our timeout
      if (error.name === 'AbortError') {
        // custom timeout

        if (verifyScreenLock(true)) {
          // Heuristic matched - treat as "locked/paused" case
          // Automatically retry when visible
          return handlePhoneLockedCase(url, opts, timeout)
        } else if (verifyScreenLock(false)) {
          // Heuristic matched - treat as "locked/paused for a long time" case
          return Promise.reject({ kind: 'phone-locked-timeout' })
        }

        return Promise.reject({ kind: 'timeout', original: error })
      }

      // TypeError -> network-level failure (includes browser net::ERR_*)
      if (error instanceof TypeError) {
        if (verifyScreenLock(true)) {
          // Heuristic matched - treat as "locked/paused" case
          // Automatically retry when visible
          return handlePhoneLockedCase(url, opts, timeout)
        } else if (verifyScreenLock(false)) {
          // Heuristic matched - treat as "locked/paused for a long time" case
          return Promise.reject({ kind: 'phone-locked-timeout' })
        }

        // Otherwise, generic network failure
        return Promise.reject({ kind: 'network', original: error })
      }

      // any other unexpected error
      return Promise.reject({ kind: 'unknown', original: error })
    }
  }

  // handlePhoneLockedCase sets up a listener for visibility changes and retries the fetch when the device becomes visible again. It also includes a timeout to give up if the device remains locked for too long.
  const handlePhoneLockedCase = (url, opts, timeout) =>
    new Promise((resolve, reject) => {
      const onVisible = () => {
        console.log('onVisible triggered, retrying fetch...')
        document.removeEventListener('visibilitychange', visibilityListener)
        // Optionally add a short backoff to give system time to restore network
        setTimeout(() => {
          lockAwareFetch(url, opts, timeout).then(resolve).catch(reject)
        }, 500) // 500ms backoff
      }

      const visibilityListener = () => {
        if (!document.hidden) onVisible()
      }

      document.addEventListener('visibilitychange', visibilityListener)

      // Also give up after some long time (optional)
      setTimeout(
        () => {
          document.removeEventListener('visibilitychange', visibilityListener)
          reject({ kind: 'phone-locked-timeout' })
        },
        2 * 60 * 1000
      ) // give up after 2 minutes

      // cleanup when resolved/rejected inside onVisible
      // (resolve/reject will naturally remove the listener and timer there)
    })

  return lockAwareFetch
}
